import { useSearch } from "@tanstack/react-router";

import { readShared } from "../../filters";
import { VBars, type BarPoint } from "../charts";
import type { PageProps } from "../index";
import { PageHead } from "../kit";
import { RowsPage, type RowsData } from "../rows";

export interface PhaseRow {
  funnel: string;
  item_id: string;
  item_name: string;
  phase: string | null;
  options_n: number;
  share: number | null;
}

/** Bars always appear in this order; a phase value outside it is added after. */
export const PHASE_ORDER: readonly string[] = [
  "1: Minimal Filter",
  "2: In-Depth Screen",
  "3: Samples + Testing",
  "4: Final Selection",
  "5: Selected",
  "ARCHIVE",
];
export const NO_PHASE = "No phase";

export interface PhaseModel {
  points: BarPoint[];
  total: number;
}

/** Share of options per phase over every row held: one item, or all items added together. */
export function phaseShares(rows: PhaseRow[]): PhaseModel {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const phase = row.phase ?? NO_PHASE;
    counts.set(phase, (counts.get(phase) ?? 0) + row.options_n);
  }
  const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);
  const extra = Array.from(counts.keys()).filter((phase) => !PHASE_ORDER.includes(phase)).sort();
  return {
    total,
    points: [...PHASE_ORDER, ...extra].map((phase) => {
      const count = counts.get(phase) ?? 0;
      return {
        label: phase,
        value: total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0,
        hint: `${phase}: ${count} options`,
      };
    }),
  };
}

export function DropPerPhaseBody({ data }: { data: RowsData<PhaseRow> }) {
  return <VBars title="Share of options per phase" slot={110} points={phaseShares(data.rows).points} format={(value) => `${value}%`} max={100} />;
}

export function DropPerPhasePage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const oneItem = readShared(search).item !== null;
  return (
    <RowsPage<PhaseRow>
      tab={tab}
      areaName={areaName}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? `Share of options in each phase · ${oneItem && data.rows[0] ? data.rows[0].item_name : "all items"}` : undefined}
          kpi={data ? { label: "Options", value: phaseShares(data.rows).total.toLocaleString("en-GB") } : null}
        />
      )}
    >
      {(data) => <DropPerPhaseBody data={data} />}
    </RowsPage>
  );
}
